import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Users, ArrowLeft, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const CreateTeam = () => {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [teamName, setTeamName] = useState('');
  const [maxMembers, setMaxMembers] = useState(10);
  const [creating, setCreating] = useState(false);

  const handleCreateTeam = async (e) => {
    e.preventDefault();

    if (!teamName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a team name",
        variant: "destructive"
      });
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/teams/create`,
        {
          name: teamName,
          max_members: maxMembers
        },
        { withCredentials: true }
      );

      toast({
        title: "Success!",
        description: "Team created successfully"
      });

      // Refresh user data to get team_id
      await checkAuth();
      
      // Navigate to team dashboard
      setTimeout(() => {
        navigate('/teams/dashboard');
      }, 500);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create team",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  if (!user) {
    navigate('/auth');
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/90 backdrop-blur-md">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-purple-600" />
            <h1 className="text-2xl font-bold">Create Your Team</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>Start Your Team Workspace</CardTitle>
            <CardDescription>
              Create a team to collaborate with colleagues, share sessions, and evaluate papers together
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTeam} className="space-y-6">
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Team Name *
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Research Lab, Innovation Team, Design Studio"
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  className="text-lg"
                  required
                />
                <p className="text-sm text-slate-500 mt-1">
                  Choose a name that represents your team's focus or organization
                </p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700 mb-2 block">
                  Maximum Team Size: <span className="text-2xl font-bold text-purple-600">{maxMembers}</span> members
                </label>
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={maxMembers}
                  onChange={(e) => setMaxMembers(parseInt(e.target.value))}
                  className="w-full h-3 bg-gradient-to-r from-purple-200 to-pink-200 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, rgb(147 51 234) 0%, rgb(147 51 234) ${(maxMembers - 1) * 11.11}%, rgb(226 232 240) ${(maxMembers - 1) * 11.11}%, rgb(226 232 240) 100%)`
                  }}
                />
                <div className="flex justify-between text-xs text-slate-500 mt-1">
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                  <span>8</span>
                  <span>9</span>
                  <span>10</span>
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Slide to select the maximum number of members for your team
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Team Features Include:</h3>
                <ul className="space-y-1 text-sm text-blue-800">
                  <li>✓ Shared workspace for IF sessions and evaluations</li>
                  <li>✓ Team analytics dashboard</li>
                  <li>✓ Invite and manage team members</li>
                  <li>✓ Collaborate on innovation projects</li>
                  <li>✓ Track team performance and OI scores</li>
                </ul>
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 text-lg py-6"
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating Team...
                  </>
                ) : (
                  <>
                    <Users className="w-5 h-5 mr-2" />
                    Create Team
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CreateTeam;
